import { formatSearchState, initSearchState } from '@faststore/sdk'
import { SearchInput as UISearchInput } from '@faststore/ui'
import { navigate } from 'gatsby'
import React, {
  useState,
  useEffect,
  Suspense,
  useRef,
  useCallback,
  useMemo,
} from 'react'

import SearchDeleteIcon from '../../../assets/SearchDeleteIcon'
import SearchIcon from '../../../assets/SearchIcon'
import { HAS_WINDOW, defaultSort } from '../../../constants'
import './search-input.scss'
import { MainSearchDropdown } from './MainSearchDropdown/MainSearchDropdown'
import { useLocalStorageContext } from '../../../contexts/LocalStorageContext'
import { useSearchOverlayContext } from '../../../contexts/SearchOverlayContext'
import useOnClickOutside from '../../../sdk/ui/useOnClickOutside'

import type {
  SearchInputRef,
  SearchInputProps as UISearchInputProps,
} from '@faststore/ui'

declare type UISearchInput = Omit<UISearchInputProps, 'onSubmit'>

interface SearchInputProps extends UISearchInput {
  isMobileNavbar?: boolean
  setShowMenu?: (showMenu: boolean) => void
}

export const doSearch = async (term: string) => {
  const { pathname, search } = formatSearchState(
    initSearchState({
      term,
      base: '/s',
    })
  )

  navigate(`${pathname}${search}`)
}

const SearchInput = (props: SearchInputProps) => {
  const [term, setTerm] = useState('')
  const [openInputDropdown, setOpenInputDropdown] = useState<boolean>(false)
  const [path, setPath] = useState(HAS_WINDOW ? window.location.pathname : '')
  const { addLastSearchedItem } = useLocalStorageContext()
  const dropdownContainer = useRef<HTMLLabelElement>(null)
  const searchInputRef = useRef<SearchInputRef>(null)
  const { setVisibleSearchOverlay } = useSearchOverlayContext()

  useOnClickOutside(dropdownContainer, () => {
    openInputDropdown && handleOpenInputDropdown(false)
  })

  const handleOpenInputDropdown = (open: boolean) => {
    setVisibleSearchOverlay?.(open)
    setOpenInputDropdown(open)
  }

  const isMultipleSearch = useMemo(() => term.includes(','), [term])

  useEffect(() => {
    if (!HAS_WINDOW) return

    const { pathname } = window.location

    if (path !== pathname) {
      setPath(pathname)

      if (pathname !== '/s/') {
        setTerm('')
      }
    }
  }, [HAS_WINDOW, window.location.pathname])

  useEffect(() => {
    if (!props.isMobileNavbar) {
    } else {
      handleOpenInputDropdown(term.length >= 3)
    }
  }, [term])

  const handleSearchTerm = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault()
    setTerm(event.target.value)
  }

  const handleSearchButton = useCallback((term: string) => {
    doSearch(term)
    handleOpenInputDropdown(false)
    props.isMobileNavbar && props.setShowMenu?.(false)
    addLastSearchedItem?.(term, `/s/?q=${term}&sort=${defaultSort}&page=0`)
    searchInputRef.current?.inputRef?.blur()
  }, [])

  const goToMultipleSearch = (term: string) =>
    navigate(`/busca-multipla/?terms=${term}&action=search`)

  const handleMultipleSearch = useCallback((term: string) => {
    goToMultipleSearch(term)
    handleOpenInputDropdown(false)
    props.isMobileNavbar && props.setShowMenu?.(false)

    const separatedTerms = term.split(',').map((term) => term.trim())

    separatedTerms.forEach((separatedTerm) => {
      addLastSearchedItem?.(
        separatedTerm,
        `/s/?q=${separatedTerm}&sort=${defaultSort}&page=0`
      )
    })
  }, [])

  return (
    <label ref={dropdownContainer} className="search-input-container">
      <UISearchInput
        onFocus={() => !props.isMobileNavbar && handleOpenInputDropdown(true)}
        icon={<SearchIcon />}
        placeholder="Pesquise por produtos ou marcas"
        onSubmit={isMultipleSearch ? handleMultipleSearch : handleSearchButton}
        value={term}
        onChange={handleSearchTerm}
        name="search"
        ref={searchInputRef}
        {...props}
      />

      {term && (
        <button className="search-input-delete-x" onClick={() => setTerm('')}>
          <SearchDeleteIcon />
        </button>
      )}

      {openInputDropdown && (
        <Suspense fallback={null}>
          <MainSearchDropdown
            setTerm={setTerm}
            setOpenInputDropdown={handleOpenInputDropdown}
            searchTerm={term}
            isMobileNavbar={props.isMobileNavbar}
            setShowMenu={props.setShowMenu}
          />
        </Suspense>
      )}
    </label>
  )
}

export default SearchInput
